import {
  api,
  type AchievementReward,
  type AlphaClubTier,
  type ClubBProgressRow,
  INCOME_TYPE_LABELS,
  INCOME_TYPES,
  type IncomeRecord,
  type IncomeType,
  queryKeys,
} from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatNumber, formatUsd } from '../../lib/format';
import { Button, Card, Chip, EmptyState, FormGroup, Input, Spinner, StatCard, StatCards } from '../../ui';

const progressCol = createColumnHelper<ClubBProgressRow>();

const PROGRESS_COLUMNS = [
  progressCol.accessor('user_code', { header: 'User Code' }),
  progressCol.accessor('name', { header: 'Name' }),
  progressCol.accessor('qualifying_count', { header: 'Qualifying Count' }),
  progressCol.accessor('next_tier_level', {
    header: 'Next Tier',
    cell: (i) => (i.getValue() != null ? `B${i.getValue()}` : '—'),
  }),
  progressCol.accessor('remaining', { header: 'Remaining' }),
];

/**
 * Club B's own payout table is legitimately empty right now (B1 needs 100
 * qualifying downline members at ALPHA 1+; nobody platform-wide is close).
 * That's mathematically correct but looks broken to an admin, so this shows
 * who IS making progress and how far they have left — client's own request:
 * "show details which id is nearest and by how much... remaining".
 */
function ClubBProgress() {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.clubBProgress(),
    queryFn: () => api.income.adminAlphaClubBProgress(),
  });

  return (
    <DataTable<ClubBProgressRow>
      title="Progress toward Club B"
      columns={PROGRESS_COLUMNS}
      data={data?.data}
      isLoading={isLoading}
      error={error as Error | null}
      emptyLabel="No members are making progress toward Club B yet"
    />
  );
}

const TABS = [
  { label: 'All', to: '/income/all' },
  ...INCOME_TYPES.map((t) => ({
    label: INCOME_TYPE_LABELS[t],
    to: `/income/${t.replace(/_/g, '-')}`,
  })),
];

const col = createColumnHelper<IncomeRecord>();

function slugToType(slug: string): IncomeType | 'all' {
  const normalised = slug.replace(/-/g, '_');
  return (INCOME_TYPES as readonly string[]).includes(normalised) ? (normalised as IncomeType) : 'all';
}

const rewardCol = createColumnHelper<AchievementReward>();

const REWARD_COLUMNS = [
  rewardCol.accessor('milestone', { header: 'Milestone' }),
  rewardCol.accessor('required_left', { header: 'Required Left' }),
  rewardCol.accessor('required_right', { header: 'Required Right' }),
  rewardCol.accessor('current_left', { header: 'Current Left' }),
  rewardCol.accessor('current_right', { header: 'Current Right' }),
  rewardCol.accessor('reward_usd', { header: 'Reward', cell: (i) => formatUsd(i.getValue()) }),
  rewardCol.accessor('achieved', {
    header: 'Status',
    cell: (i) => (i.getValue() ? <Chip tone="success">Achieved</Chip> : <Chip tone="gold">Pending</Chip>),
  }),
];

const clubACol = createColumnHelper<AlphaClubTier>();

const CLUB_A_COLUMNS = [
  clubACol.accessor('rank_code', { header: 'Rank' }),
  clubACol.accessor('required_count', { header: 'Required' }),
  clubACol.accessor('achieved_count', { header: 'Achieved So Far' }),
  clubACol.accessor('gift_usd', { header: 'Gift', cell: (i) => formatUsd(i.getValue()) }),
  clubACol.accessor('achieved', {
    header: 'Status',
    cell: (i) => (i.getValue() ? <Chip tone="success">Achieved</Chip> : <Chip tone="gold">Pending</Chip>),
  }),
];

/**
 * Admin's member-ID search box for the Achievement Rewards tracker (client
 * requirement) — mirrors the ID-search pattern from the admin Binary Tree
 * page (pages/team/BinaryTree.tsx). Also the ONE tracker view every
 * member-ID link in the admin panel points at: arriving here with
 * ?member=<code> in the URL (e.g. clicked from the Members list, or from the
 * User ID / From columns just below) pre-fills the box and fetches
 * immediately, rather than each place duplicating this view.
 */
function MemberRewardTracker() {
  const [searchParams, setSearchParams] = useSearchParams();
  const memberParam = searchParams.get('member') ?? '';

  const [inputId, setInputId] = useState(memberParam);
  const [activeId, setActiveId] = useState<string | undefined>(memberParam || undefined);

  // A fresh navigation to ?member=<code> (from elsewhere in the admin panel)
  // should re-trigger the lookup even if this component is already mounted.
  useEffect(() => {
    if (memberParam && memberParam !== activeId) {
      setInputId(memberParam);
      setActiveId(memberParam);
    }
  }, [memberParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.rewardTracker(activeId ?? ''),
    queryFn: () => api.income.adminRewardTracker(activeId!).then((r) => r.data),
    enabled: !!activeId,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = inputId.trim();
    if (!id) return;
    setActiveId(id);
    setSearchParams({ member: id });
  };

  return (
    <>
      <Card>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 1 220px', minWidth: 180 }}>
            <FormGroup label="Member ID">
              <Input
                placeholder="e.g. AO769435"
                value={inputId}
                onChange={(e) => setInputId(e.target.value)}
              />
            </FormGroup>
          </div>
          <Button type="submit" variant="gold">
            View Reward Tracker
          </Button>
        </form>
      </Card>

      {activeId ? (
        isLoading ? (
          <Card>
            <Spinner />
          </Card>
        ) : error ? (
          <Card>
            <EmptyState title="That member ID could not be found" />
          </Card>
        ) : data ? (
          <>
            <Card>
              <h3 style={{ margin: '0 0 16px' }}>
                {data.name} — {data.user_code}
              </h3>
              <StatCards>
                <StatCard label="Left Active" value={formatNumber(data.left_active)} />
                <StatCard label="Left Inactive" value={formatNumber(data.left_inactive)} />
                <StatCard label="Left Total" value={formatNumber(data.left_total)} />
                <StatCard label="Right Active" value={formatNumber(data.right_active)} />
                <StatCard label="Right Inactive" value={formatNumber(data.right_inactive)} />
                <StatCard label="Right Total" value={formatNumber(data.right_total)} />
                <StatCard label="Matched" value={formatNumber(data.matched)} />
                <StatCard label="Remaining for Next Reward" value={formatNumber(data.next_reward_remaining)} />
              </StatCards>
            </Card>
            <DataTable<AchievementReward>
              title="Achievement Reward Tiers"
              columns={REWARD_COLUMNS}
              data={data.rewards}
              emptyLabel="No active reward tiers configured"
            />

            <Card>
              <StatCards>
                <StatCard label="Remaining for Next Alpha Club A Tier" value={formatNumber(data.alpha_club_a_remaining)} />
              </StatCards>
            </Card>
            <DataTable<AlphaClubTier>
              title="Alpha Club A Tiers"
              columns={CLUB_A_COLUMNS}
              data={data.alpha_club_a}
              emptyLabel="No active Alpha Club A tiers configured"
            />
          </>
        ) : null
      ) : null}
    </>
  );
}

/**
 * One ledger browser serves every Alpha One income type. The type comes from
 * the route and is passed straight through to the server — no client-side
 * filtering of a full dataset.
 */
export function IncomeLedger() {
  const { type: slug = 'all' } = useParams();
  const type = slugToType(slug);
  const { query, setPage } = useTableQuery({ defaultSort: 'created_at' });

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.income(type, query),
    queryFn: () => api.income.adminList(type, query),
  });

  const columns = useMemo(
    () => [
      col.accessor('user_code', {
        header: 'User ID',
        // Opens this member's Achievement Reward tracker — client requirement
        // that any member ID click in the admin panel opens the tracker.
        cell: (i) =>
          i.getValue() ? (
            <Link to={`/income/achievement-reward?member=${i.getValue()}`}>{i.getValue()}</Link>
          ) : (
            '—'
          ),
      }),
      col.accessor('type', { header: 'Type', cell: (i) => INCOME_TYPE_LABELS[i.getValue()] }),
      col.accessor('from_user_code', {
        header: 'From',
        cell: (i) =>
          i.getValue() ? (
            <Link to={`/income/achievement-reward?member=${i.getValue()}`}>{i.getValue()}</Link>
          ) : (
            '—'
          ),
      }),
      col.accessor('level', { header: 'Level', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'USD', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: 'LDS', cell: (i) => formatLds(i.getValue()) }),
      col.accessor('description', { header: 'Note', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('created_at', { header: 'Credited', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Income Ledger"
        description={type === 'all' ? 'All income types' : INCOME_TYPE_LABELS[type]}
        tabs={TABS}
      />
      <DataTable<IncomeRecord>
        title={type === 'all' ? 'All Income' : INCOME_TYPE_LABELS[type]}
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />

      {type === 'alpha_club_b' ? <ClubBProgress /> : null}
      {type === 'achievement_reward' ? <MemberRewardTracker /> : null}
    </>
  );
}
